import { useState } from 'react'
import { format } from '@/utils'
import { API_URL } from '@/utils/env'
import { getCompanyShortName, getLoginToken } from '@/modules/login/util'
import type {
  DownloadTachoFilesMode,
  DownloadTachoFilesResult,
  DownloadTachoFilesSelection,
} from './types'

interface EndpointConfig {
  path: string
  idParam: string
}

const ENDPOINTS: Record<DownloadTachoFilesMode, EndpointConfig> = {
  driver: {
    path: 'tacho/driver-files/download-zip/',
    idParam: 'user_id',
  },
  vehicle: {
    path: 'tacho/vehicle-files/download-zip/',
    idParam: 'vehicle_id',
  },
}

const OBJECT_URL_REVOKE_DELAY_MS = 60_000

/**
 * Requests a ZIP of tacho files for the given timespan/selection from the
 * backend and saves it. The endpoint is private, so the request carries the
 * same auth + company headers Apollo uses (see src/apollo.ts).
 */
export function useDownloadTachoFilesZip(mode: DownloadTachoFilesMode) {
  const [downloading, setDownloading] = useState(false)

  async function download(
    selection: DownloadTachoFilesSelection
  ): Promise<DownloadTachoFilesResult> {
    const { path, idParam } = ENDPOINTS[mode]

    const start = format(selection.dateStart, 'yyyy-MM-dd')
    const end = format(selection.dateEnd, 'yyyy-MM-dd')
    const fileName = `tacho-${mode}-files_${start}_${end}.zip`

    const params = new URLSearchParams()
    params.set('date_start', start)
    params.set('date_end', end)
    selection.ids.forEach(id => params.append(idParam, id))

    const companyShortName = getCompanyShortName()
    const headers: Record<string, string> = {
      Authorization: `Bearer ${getLoginToken()}`,
    }
    if (companyShortName) headers['X-Firmadok-Company'] = companyShortName

    try {
      setDownloading(true)
      const response = await fetch(`${API_URL}/${path}?${params.toString()}`, {
        headers,
      })
      // 204 = no files matched the filters; nothing to save.
      if (response.status === 204) return { ok: true, empty: true }
      if (!response.ok) return { ok: false, empty: false }

      const blob = await response.blob()
      if (blob.size === 0) return { ok: false, empty: false }

      const objectUrl = URL.createObjectURL(blob)

      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = fileName
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      window.setTimeout(() => {
        URL.revokeObjectURL(objectUrl)
      }, OBJECT_URL_REVOKE_DELAY_MS)
      return { ok: true, empty: false }
    } finally {
      setDownloading(false)
    }
  }

  return { download, downloading }
}
